import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import api from '../utils/api';
import { gettext, truncateWords } from '../utils/text';
import Loading from './Loading';
import TrafoTextPreview from './arena/TrafoTextPreview';

class TrafoPreview extends React.Component {
    static propTypes = {
        mediaItem: PropTypes.shape({
            id: PropTypes.number.isRequired,
            title: PropTypes.string,
            description: PropTypes.string,
            allow_comments: PropTypes.bool,
            audioUrl: PropTypes.string,
            videoUrl: PropTypes.string,
            objectUrl: PropTypes.string,
            isVideo: PropTypes.bool,
            isAudio: PropTypes.bool,
            isPhoto: PropTypes.bool,
            isText: PropTypes.bool,
            isPdf: PropTypes.bool,
            pdfNumberOfPages: PropTypes.number,
        }),
        file: PropTypes.instanceOf(File),
        size: PropTypes.oneOf(['small', 'medium', 'large', null]),
        finishedLoadingCallback: PropTypes.func,
        showNewlyPublished: PropTypes.bool,
        showUnavailable: PropTypes.bool,
        showPreview: PropTypes.bool,
        onClick: PropTypes.func,
        className: PropTypes.string,
    };
    static defaultProps = {
        mediaItem: null,
        file: null,
        size: null,
        finishedLoadingCallback: undefined,
        showNewlyPublished: false,
        showUnavailable: false,
        showPreview: false,
        onClick: undefined,
        className: '',
    };

    // Retry 4 times over ~20 seconds
    static RETRY_TIMEOUTS = [1000, 3000, 5000, 10000];

    constructor(props) {
        super(props);
        const initialState = {
            isLoading: true,
            timesTried: 0,
            thumbnailUrl: undefined,
            text: undefined,
            textProperties: undefined,
        };
        if (props.file && props.file.type.startsWith('image')) {
            initialState.thumbnailUrl = props.file.preview;
        }

        this.state = initialState;
    }

    componentDidMount() {
        if (!this.state.thumbnailUrl && this.props.mediaItem && this.props.mediaItem.id) {
            this.fetchPreview();
        }
    }

    componentWillUnmount() {
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
        }
        // Revoke preview to avoid memory leaks
        if (this.props.file) {
            window.URL.revokeObjectURL(this.props.file);
        }
    }

    getTitle() {
        if (this.props.file && this.props.file.name) {
            return this.props.file.name;
        } else if (this.props.mediaItem && this.props.mediaItem.title) {
            return this.props.mediaItem.title;
        }
        return gettext('Preview');
    }

    fetchPreview() {
        api.mediaPreviewDetail.fetch({ pk: this.props.mediaItem.id }, { size: this.props.size }).then((response) => {
            if (response.icon_class && response.icon_class.includes('fa-spinner')) {
                if (this.state.timesTried < TrafoPreview.RETRY_TIMEOUTS.length) {
                    this.retryTimeout = setTimeout(
                        () => this.fetchPreview(),
                        TrafoPreview.RETRY_TIMEOUTS[this.state.timesTried],
                    );
                    this.setState((currentState) => ({
                        isLoading: true,
                        timesTried: currentState.timesTried + 1,
                    }));
                } else {
                    // Give up after retrying a couple of times
                    this.setState({ isLoading: false });
                }
            } else {
                this.setState({
                    thumbnailUrl: response.thumbnail_url,
                    text: this.props.size === 'large' ? response.text : truncateWords(response.text, 20),
                    textProperties: response.text_context,
                });
            }
            if (this.props.finishedLoadingCallback) {
                this.props.finishedLoadingCallback(!!this.props.onClick);
            }
        });
    }

    render() {
        let content = '';
        const { mediaItem, showPreview, size, showUnavailable, showNewlyPublished, onClick, className } = this.props;
        if (this.state.thumbnailUrl) {
            if (mediaItem && mediaItem.isVideo) {
                // width:100% and height:100% do not force mediaplayer to resize properly if put into stylesheet
                content = (
                    <div className="trafo-artwork-preview-video">
                        <video
                            className={classNames({
                                preview: showPreview,
                            })}
                            src={mediaItem.videoUrl}
                            poster={this.state.thumbnailUrl}
                            title={this.getTitle()}
                            width="100%"
                            height="100%"
                        />
                    </div>
                );
            } else if (mediaItem && mediaItem.isAudio) {
                content = (
                    <div className="trafo-artwork-preview-audio">
                        <div
                            className="trafo-artwork-preview-audioimage"
                            title={this.getTitle()}
                            style={{ backgroundImage: `url(${this.state.thumbnailUrl})` }}
                        />
                        <audio
                            className={classNames({
                                preview: showPreview,
                            })}
                            src={mediaItem.audioUrl}
                        />
                    </div>
                );
            } else {
                content = <img className="trafo-preview__image" src={this.state.thumbnailUrl} alt={this.getTitle()} />;
            }
            if (mediaItem && mediaItem.objectUrl && !onClick) {
                const isPDF = mediaItem.isPdf;
                const className = classNames({
                    'trafo-preview__pdf-link': isPDF,
                    'trafo-preview__artwork-link': !isPDF,
                    preview: showPreview && !isPDF,
                });
                const newTab = isPDF ? '_blank' : '';
                const pdfReadMoreButton =
                    isPDF && mediaItem.pdfNumberOfPages > 1 && showPreview ? (
                        <button className="btn btn-primary mb-0 border-0">{gettext('Read more')}</button>
                    ) : (
                        ''
                    );
                content = (
                    <a href={mediaItem.objectUrl} className={className} target={newTab}>
                        {content}
                        {pdfReadMoreButton}
                    </a>
                );
            }
        } else if (this.state.text) {
            if (size === 'large') {
                const { textProperties } = this.state;
                content = (
                    <TrafoTextPreview
                        className={'m-3'}
                        title={undefined}
                        text={this.state.text}
                        align={textProperties ? textProperties.text_align : 'left'}
                        font={textProperties ? textProperties.text_font : 'sans-small'}
                        lineHeight={textProperties ? textProperties.text_lineheight : 'small'}
                    />
                );
            } else {
                content = <div className="trafo-preview__text">{this.state.text}</div>;
            }
        } else if (this.state.isLoading) {
            content = <Loading />;
        }
        if (content === '' && showUnavailable) {
            content = (
                <div className="trafo-preview__unavailable">{gettext('Sorry! The preview is not available.')}</div>
            );
        }
        return (
            <div
                className={classNames(className, {
                    'trafo-preview': true,
                    'trafo-preview--small': size === 'small',
                    'trafo-preview--medium': size === 'medium',
                    'trafo-preview--large': size === 'large',
                })}
                onClick={onClick}
            >
                {showNewlyPublished ? (
                    <div className="trafo-preview__media-published">
                        {gettext('Congratulations. Your media is published!')}
                    </div>
                ) : undefined}
                {content}
            </div>
        );
    }
}

export default TrafoPreview;



// WEBPACK FOOTER //
// ./src/components/TrafoPreview.js